import { useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Card,
  Alert,
  Form,
  Button,
  Tabs,
  Tab,
  Row,
  Col,
  Tooltip,
} from "react-bootstrap";
import {
  showToast,
  parseChain,
  downloadOrCopy,
  prettyTime,
  copyToClipboard,
  prettyCode,
  boxedContent,
  withTooltipDescription,
} from "../utils/utils";
import { Link } from "react-router-dom";
import { errorToString } from "../utils/error";
import { getApi } from "../axios";
import { signerUsageExample } from "./Docs";
import SignerModal from "./SignerModal";
import JSONModal from "./JSONModal";
import KeyValueTable from "./KeyValueTable";

function SignerDetails({
  isLoading,
  setIsLoading,
  setTitle,
  setSubtitle,
  setDropdownActions,
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const signerName =
    decodeURIComponent(location.pathname.replaceAll("/signers/", "")) || "";

  const [refreshTrigger, setRefreshTrigger] = useState(0); // used to trigger refresh of signerConfig after edit, by changing the key of ConfigTab

  // these are all loaded from the CA Chain Tab,
  // but we need them here in the parent component to pass down to other tabs
  const [hasWarnings, setHasWarnings] = useState(false);
  const [canSign, setCanSign] = useState(false);
  const [signerConfig, setSignerConfig] = useState(null);

  const api = getApi();

  const apiUrl = new URL(api.defaults.baseURL);
  const apiRootUrl = apiUrl.href.replace(/\/v1\/?$/, "");

  const [error, setError] = useState("");

  const [showSignerModal, setShowSignerModal] = useState(false);

  useEffect(() => {
    setTitle(signerName);
  }, [signerName, setTitle, signerConfig]);

  useEffect(() => {
    setSubtitle(
      signerConfig ? (
        <div>
          <i className="bi bi-award me-1"></i>{" "}
          {signerConfig?.caTemplate?.subject?.commonName}
        </div>
      ) : null,
    );
  }, [signerConfig, setSubtitle]);

  const handleDeleteSigner = useCallback(async () => {
    // ask user to enter the signer name to confirm deletion
    const confirmedName = window.prompt(
      `To confirm deletion, please enter the signer name: ${signerName}`,
    );

    // Cancel pressed: do nothing
    if (confirmedName === null) {
      return;
    }

    if (confirmedName !== signerName) {
      showToast("error", "Signer name does not match. Deletion cancelled.");
      return;
    }

    setIsLoading(true);
    try {
      await api.delete(`/signers/${signerName}`);
      navigate("/signers");
      showToast("success", "Signer deleted!");
    } catch (err) {
      showToast("error", errorToString(err));
    } finally {
      setIsLoading(false);
    }
  }, [api, navigate, signerName]);

  useEffect(() => {
    setDropdownActions?.([
      {
        key: "edit-signer",
        label: "Edit",
        iconClass: "bi bi-pencil-square",
        onClick: () => setShowSignerModal(true),
      },
      {
        key: "delete-signer",
        label: "Delete",
        iconClass: "bi bi-trash",
        onClick: handleDeleteSigner,
      },
    ]);
    return () => setDropdownActions?.([]);
  }, [handleDeleteSigner, setDropdownActions]);

  return (
    <>
      <SignerModal
        show={showSignerModal}
        onHide={() => setShowSignerModal(false)}
        editMode={true}
        onSuccess={(updatedSigner) => {
          setRefreshTrigger((prev) => prev + 1);
        }}
      />

      {error && <Alert variant="danger">{error || "An error occurred."}</Alert>}

      <Tabs className="mb-3" key={`signer-details-${refreshTrigger}`}>
        {/* Config Tab */}
        <Tab eventKey="config" title="Configuration">
          <ConfigTab
            signerName={signerName}
            isLoading={isLoading}
            signerConfig={signerConfig}
            setSignerConfig={setSignerConfig}
            setIsLoading={setIsLoading}
            setError={setError}
          />
        </Tab>

        {/* CA CSR & Chain Tab */}
        <Tab
          eventKey="chain"
          title={
            <>
              CA CSR & Chain{" "}
              {hasWarnings && (
                <i className="bi bi-exclamation-triangle ms-1"></i>
              )}
            </>
          }
        >
          <CSRAndChainTab
            signerName={signerName}
            isLoading={isLoading}
            setIsLoading={setIsLoading}
            setHasWarnings={setHasWarnings}
            setCanSign={setCanSign}
          />
        </Tab>

        {/* Sign Certificate Tab */}
        <Tab eventKey="sign" title="Sign Certificate" disabled={!canSign}>
          <SignCertificateTab
            signerName={signerName}
            isLoading={isLoading}
            setIsLoading={setIsLoading}
          />
        </Tab>

        {/* Revoke Tab */}
        <Tab eventKey="revoke" title="Revoke Certificate" disabled={!canSign}>
          <RevokeCertificateTab
            signerName={signerName}
            isLoading={isLoading}
            setIsLoading={setIsLoading}
          />
        </Tab>

        {/* Usage Examples Tab */}
        <Tab
          eventKey="examples"
          title="Usage Examples"
          disabled={signerConfig?.isCA}
        >
          {signerUsageExample(signerName, apiRootUrl)}
        </Tab>
      </Tabs>
    </>
  );
}

function ConfigTab({
  signerName,
  signerConfig,
  setSignerConfig,
  isLoading,
  setIsLoading,
  setError,
}) {
  const [privateKeyConfig, setPrivateKeyConfig] = useState(null);

  const [showKeyModal, setShowKeyModal] = useState(false);
  const [keyModalTitle, setKeyModalTitle] = useState("");
  const [keyModalData, setKeyModalData] = useState(null);

  const api = getApi();

  const fetchConfig = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await api.get(`/signers/${signerName}/config`);
      setSignerConfig(res.data || {});
    } catch (err) {
      //showToast("error", errorToString(err));
      setError(errorToString(err));
      navigation.navigate("/signers");
    } finally {
      setIsLoading(false);
    }
  }, [api, signerName, setIsLoading, setSignerConfig, setError]);

  const fetchPrivateKeyConfig = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await api.get(`/signers/${signerName}/private-key`);
      const privateKeyID = res.data;
      const res2 = await api.get(`/keys/${privateKeyID}`);
      setPrivateKeyConfig(res2.data);
    } catch (err) {
      //showToast("error", errorToString(err));
      setError(errorToString(err));
    } finally {
      setIsLoading(false);
    }
  }, [api, signerName, setIsLoading, setError]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  useEffect(() => {
    fetchPrivateKeyConfig();
  }, [fetchPrivateKeyConfig]);

  const handleShowKey = async (keyID) => {
    setIsLoading(true);
    try {
      const res = await api.get(`/keys/${keyID}`);
      setKeyModalData(res.data);
      setKeyModalTitle(`Private Key: ${keyID}`);
      setShowKeyModal(true);
    } catch (err) {
      setError(errorToString(err));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    signerConfig &&
    privateKeyConfig && (
      <>
        <JSONModal
          show={showKeyModal}
          onHide={() => setShowKeyModal(false)}
          modalTitle={keyModalTitle}
          modalData={keyModalData}
        />

        <Row>
          {/* Private Key Section */}
          <Col md={6} className="mb-3">
            <Card>
              <Card.Header>
                <h6 className="mb-0">Private Key</h6>
                <div className="text-muted small">
                  Key used by for signing operations
                </div>
              </Card.Header>
              <Card.Body>
                <KeyValueTable
                  borderBottom={false}
                  striped={false}
                  body={{
                    ID: (
                      <Link
                        style={{ textDecoration: "none" }}
                        className="text-body"
                        onClick={() => handleShowKey(privateKeyConfig.id)}
                      >
                        {privateKeyConfig.id}
                      </Link>
                    ),
                    Type: (
                      <div className="d-flex align-items-center gap-2">
                        <div>{privateKeyConfig.config.type}</div>
                        <div>
                          {privateKeyConfig.config.pkcs11URI ? (
                            <div className="d-flex gap-2 align-items-center">
                              <i className="bi bi-safe"></i>HSM
                            </div>
                          ) : (
                            <div className="d-flex gap-2 align-items-center">
                              <i className="bi bi-laptop"></i>Software
                            </div>
                          )}
                        </div>
                      </div>
                    ),
                    Bits: privateKeyConfig.config.bits || null,
                    Curve: privateKeyConfig.config.curve || null,
                    // "PKCS11 URI": privateKeyConfig.config.pkcs11URI || null,
                    // "PKCS11 Key URI": privateKeyConfig.config.pkcs11KeyURI || null,
                  }}
                  keysClass="fw-bold"
                />
              </Card.Body>
            </Card>
          </Col>

          {/* CA Template Section */}
          <Col md={6} className="mb-3">
            <Card>
              <Card.Header>
                <h6 className="mb-0">CA Template</h6>
                <div className="text-muted small">
                  Fields of the certificate template used when creating CA CSRs
                </div>
              </Card.Header>
              <Card.Body>
                <KeyValueTable
                  borderBottom={false}
                  striped={false}
                  body={{
                    "Common Name": signerConfig.caTemplate.subject.commonName,
                    Country: signerConfig.caTemplate.subject.country
                      ? signerConfig.caTemplate.subject.country.join(", ")
                      : null,
                    Organization: signerConfig.caTemplate.subject.organization
                      ? signerConfig.caTemplate.subject.organization.join(", ")
                      : null,
                    "Organizational Unit": signerConfig.caTemplate.subject
                      .organizationalUnit
                      ? signerConfig.caTemplate.subject.organizationalUnit.join(
                          ", ",
                        )
                      : null,
                    Locality: signerConfig.caTemplate.subject.locality
                      ? signerConfig.caTemplate.subject.locality.join(", ")
                      : null,
                    Province: signerConfig.caTemplate.subject.province
                      ? signerConfig.caTemplate.subject.province.join(", ")
                      : null,
                    "Street Address": signerConfig.caTemplate.subject
                      .streetAddress
                      ? signerConfig.caTemplate.subject.streetAddress.join(", ")
                      : null,
                    "Postal Code": signerConfig.caTemplate.subject.postalCode
                      ? signerConfig.caTemplate.subject.postalCode.join(", ")
                      : null,
                  }}
                  keysClass="fw-bold"
                />
              </Card.Body>
            </Card>
          </Col>

          {/* Certificate Template Section */}
          <Col md={6} className="mb-3">
            <Card>
              <Card.Header>
                <h6 className="mb-0">Certificate Template</h6>
                <div className="text-muted small">
                  Fields included in the certificates issued by this signer
                </div>
              </Card.Header>
              <Card.Body>
                <KeyValueTable
                  borderBottom={false}
                  striped={false}
                  body={{
                    "Is CA": signerConfig.isCA ? "Yes" : "No",
                    CDP:
                      signerConfig.cdp?.length > 0 ? (
                        <>
                          {signerConfig.cdp.map((url, index) => (
                            <div
                              className="d-flex justify-content-between align-items-center gap-2"
                              key={index}
                            >
                              <span
                                className="text-truncate"
                                style={{ maxWidth: "24rem" }}
                              >
                                {url}
                              </span>
                              <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={() => {
                                  copyToClipboard(url, "CDP URL copied!");
                                }}
                              >
                                <i className="bi bi-clipboard"></i>
                              </Button>
                            </div>
                          ))}
                        </>
                      ) : (
                        "-"
                      ),
                    AIA:
                      signerConfig.aia?.length > 0 ? (
                        <>
                          {signerConfig.aia.map((url, index) => (
                            <div
                              className="d-flex justify-content-between align-items-center gap-2"
                              key={index}
                            >
                              <span
                                className="text-truncate"
                                style={{ maxWidth: "24rem" }}
                              >
                                {url}
                              </span>
                              <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={() => {
                                  copyToClipboard(url, "AIA URL copied!");
                                }}
                              >
                                <i className="bi bi-clipboard"></i>
                              </Button>
                            </div>
                          ))}
                        </>
                      ) : null,
                    OCSP:
                      signerConfig.ocsp?.length > 0 ? (
                        <>
                          {signerConfig.ocsp.map((url, index) => (
                            <div
                              className="d-flex justify-content-between align-items-center gap-2"
                              key={index}
                            >
                              <span
                                className="text-truncate"
                                style={{ maxWidth: "24rem" }}
                              >
                                {url}
                              </span>
                              <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={() => {
                                  copyToClipboard(url, "OCSP URL copied!");
                                }}
                              >
                                <i className="bi bi-clipboard"></i>
                              </Button>
                            </div>
                          ))}
                        </>
                      ) : null,
                  }}
                  keysClass="fw-bold"
                />
              </Card.Body>
            </Card>
          </Col>

          {/* Policy Section */}
          <Col md={6} className="mb-3">
            <Card>
              <Card.Header>
                <h6 className="mb-0">Policy</h6>
                <div className="text-muted small">
                  Rules that control what this signer is allowed to issue and
                  how it operates
                </div>
              </Card.Header>
              <Card.Body>
                <KeyValueTable
                  borderBottom={false}
                  striped={false}
                  body={{
                    "Allowed Key Usages":
                      signerConfig.allowedKeyUsages?.length > 0
                        ? signerConfig.allowedKeyUsages.join(", ")
                        : "-",
                    "Allowed Domains":
                      signerConfig.allowedDomains?.length > 0
                        ? signerConfig.allowedDomains.join(", ")
                        : "-",
                    "Max TTL": signerConfig.maxTTL,
                  }}
                  keysClass="fw-bold"
                />
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </>
    )
  );
}

function CSRAndChainTab({
  signerName,
  isLoading,
  setIsLoading,
  setHasWarnings,
  setCanSign,
}) {
  const [caCSR, setCACSR] = useState(null);
  const [caChain, setCAChain] = useState(null);
  const [signerConfig, setSignerConfig] = useState(null);
  const [newChain, setNewChain] = useState("");
  const [warnings, setWarnings] = useState([]);

  const api = getApi();

  const fecthConfig = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await api.get(`/signers/${signerName}/config`);
      setSignerConfig(res.data || null);
    } catch (err) {
      showToast("error", errorToString(err));
    } finally {
      setIsLoading(false);
    }
  }, [api, signerName]);

  const fetchCAChain = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await api.get(`/signers/${signerName}/ca-chain`);
      if (res.data) {
        setCAChain(parseChain(res.data));
      }
    } catch (err) {
      showToast("error", errorToString(err));
    } finally {
      setIsLoading(false);
    }
  }, [api, signerName, setIsLoading]);

  useEffect(() => {
    fecthConfig();
  }, [fecthConfig]);

  const handleSetChain = async () => {
    setIsLoading(true);
    try {
      await api.put(`/signers/${signerName}/ca-chain`, newChain);
      setCAChain(parseChain(newChain));
      setNewChain("");
      showToast("success", "CA chain updated!");
    } catch (err) {
      showToast("error", errorToString(err));
    } finally {
      setIsLoading(false);
    }
  };

  const createWarnings = useCallback(() => {
    let warns = [];
    let canSign = true;

    // No CA chain
    if (!caChain || caChain.length === 0) {
      canSign = false;
      if (signerConfig?.isCA) {
        warns.push(
          "No CA chain found, thus can only sign certificates for self.",
        );
        canSign = true; // allow self-signing but with warning
      } else
        warns.push(
          "No CA chain found and signer is not a CA signer, thus cannot sign any certificates.",
        );
    }

    // Check for mismatch between signerConfig and caChain[0]
    if (
      signerConfig?.caTemplate?.subject?.commonName &&
      caChain &&
      caChain.length > 0
    ) {
      const certCN = caChain[0].subject.match(/CN=([^,/]+)/);
      const caTemplateCN = signerConfig.caTemplate.subject.commonName;

      if (caTemplateCN !== certCN?.[1]) {
        warns.push(
          `The subject in the CA certificate ('${certCN?.[1]}') does not match the one in the CA template ('${caTemplateCN}'). This is likely because the signer CA template was updated after the CA chain was last set.`,
        );
      }
    }

    // Check for expiration soon
    if (caChain && caChain.length > 0) {
      const now = new Date();
      const soon = new Date();
      soon.setDate(now.getDate() + 30); // 30 days from now

      const notAfter = new Date(caChain[0].notAfter);
      if (notAfter < now) {
        warns.push("The CA certificate has expired.");
      } else if (notAfter < soon) {
        warns.push(
          `The CA certificate will expire soon, on ${prettyTime(notAfter)}.`,
        );
      }
    }

    if (caChain && caChain.length > 0) {
      const lastCert = caChain[caChain.length - 1];
      if (lastCert.issuer !== lastCert.subject) {
        warns.push(
          "The CA chain does not end with a self-signed (root) certificate, which may cause trust issues for some clients.",
        );
      }
    }
    setWarnings(warns);
    setHasWarnings(warns.length > 0);
    setCanSign(canSign);
  }, [caChain, signerConfig, setHasWarnings, setCanSign]);

  useEffect(() => {
    fetchCAChain();
  }, [fetchCAChain]);

  useEffect(() => {
    createWarnings();
  }, [createWarnings]);

  const handleCreateCSR = async () => {
    setIsLoading(true);
    try {
      const response = await api.get(`/signers/${signerName}/ca-csr`);
      setCACSR(response.data);
      // showToast("success", "CA CSR created!");
    } catch (err) {
      showToast("error", errorToString(err));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Row>
      {/* Show warnings if any */}
      {warnings?.map((w, i) => (
        <Col md={12} key={i}>
          <Alert variant="warning">{w}</Alert>
        </Col>
      ))}

      {/* Create CA CSR */}
      <Col md={12} className="mb-3">
        <Button onClick={() => handleCreateCSR()} disabled={isLoading}>
          Create CA CSR
        </Button>
        {caCSR &&
          downloadOrCopy(
            "CA CSR created! Next, have it signed and use the signed certificate with full CA chain to update this signer's CA chain.",
            caCSR,
            "ca-csr.pem",
            "mt-3 mb-0",
          )}
      </Col>

      {/* Show CA Chain */}
      {caChain?.length > 0 && (
        <Col md={6} className="mb-3">
          {[...caChain].map((cert, index) => {
            return (
              <Card className="mb-3" key={index}>
                <Card.Header>
                  <h6 className="mb-0">Certificate {index + 1}</h6>
                  <div className="text-muted small">
                    {cert.subject === cert.issuer ? "Root" : "Intermediate"}
                  </div>
                </Card.Header>
                <Card.Body>
                  <KeyValueTable
                    borderBottom={false}
                    striped={false}
                    body={{
                      Serial: cert.serial,
                      Subject: cert.subject,
                      Issuer: cert.issuer,
                      "Not Before": prettyTime(cert.notBefore),
                      "Not After": prettyTime(cert.notAfter),
                      PEM: (
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => {
                            copyToClipboard(cert.pem, "PEM copied!");
                          }}
                        >
                          <i className="bi bi-clipboard"></i>
                        </Button>
                      ),
                    }}
                    keysClass="fw-bold"
                  />
                </Card.Body>
              </Card>
            );
          })}
        </Col>
      )}

      {/* Update CA Chain */}
      <Col md={caChain?.length > 0 ? 6 : 12} className="mb-3">
        <Form.Group className="mb-3">
          <Form.Control
            as="textarea"
            rows={10}
            placeholder="Enter PEM-encoded chain of certificates"
            value={newChain}
            onChange={(e) => setNewChain(e.target.value)}
          />
        </Form.Group>
        <div className="d-flex justify-content-end">
          <Button
            variant="primary"
            onClick={handleSetChain}
            disabled={!newChain || isLoading}
          >
            Update CA Chain
          </Button>
        </div>
      </Col>
    </Row>
  );
}

function SignCertificateTab({ signerName, isLoading, setIsLoading }) {
  const [csr, setCSR] = useState("");
  const [ttl, setTTL] = useState(720);
  const [comment, setComment] = useState("");
  const [signedCert, setSignedCert] = useState("");
  const api = getApi();

  useEffect(() => {
    setCSR("");
    setTTL(720);
    setComment("");
    setSignedCert("");
  }, [signerName]);

  const handleSignCertificate = async () => {
    setIsLoading(true);
    try {
      const response = await api.post(`/signers/${signerName}/sign`, {
        csr,
        ttl: `${ttl}h`,
        comment,
      });
      setCSR("");
      setTTL(720);
      setComment("");
      // full chain is certificate + ca_chain[:]
      const fullChain = [
        response.data.data.certificate,
        ...(response.data.data.ca_chain || []),
      ];
      // string join with newline to get PEM format
      setSignedCert(fullChain.join("\n"));
    } catch (err) {
      showToast("error", errorToString(err));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Form>
        <Row>
          <Col md={6} className="mb-3">
            <Form.Group>
              <Form.Label>CSR</Form.Label>
              <Form.Control
                as="textarea"
                rows={10}
                value={csr}
                placeholder="Enter PEM-encoded request"
                onChange={(e) => setCSR(e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col
            md={6}
            className="mb-3 d-flex flex-column justify-content-between gap-3"
          >
            <Form.Group>
              <Form.Label>TTL</Form.Label>
              <div className="input-group">
                <Form.Control
                  type="number"
                  placeholder="e.g. 8760"
                  value={ttl}
                  onChange={(e) => setTTL(Number(e.target.value))}
                />
                <span className="input-group-text">hours</span>
              </div>
            </Form.Group>
            <Form.Group>
              <Form.Label>Comment</Form.Label>
              <Form.Control
                as="textarea"
                rows={6}
                placeholder="Optional comment (e.g. 'Issued for Alice's laptop')"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col md={12} className="d-flex justify-content-end mb-3">
            <Form.Group>
              <Button
                variant="primary"
                onClick={handleSignCertificate}
                disabled={!csr || ttl === 0}
              >
                Sign Certificate
              </Button>
            </Form.Group>
          </Col>
        </Row>
      </Form>

      {/* {signedCert && prettyCode("pem", signedCert)} */}
      {signedCert &&
        downloadOrCopy("Certificate signed!", signedCert, "cert.pem")}
    </>
  );
}

function RevokeCertificateTab({ signerName, isLoading, setIsLoading }) {
  const [serial, setSerial] = useState("");
  const [reason, setReason] = useState(0);
  const api = getApi();

  useEffect(() => {
    setSerial("");
    setReason(0);
  }, [signerName]);

  const handleRevoke = async () => {
    if (!serial.trim()) return;

    const confirmed = window.confirm(
      `Revoke certificate with serial ${serial}?`,
    );
    if (!confirmed) return;

    setIsLoading(true);
    try {
      await api.post(`/signers/${signerName}/revoke`, { serial, reason });
      setSerial("");
      setReason(0);
      showToast("success", "Certificate revoked!");
    } catch (err) {
      showToast("error", errorToString(err));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Form>
      <Row>
        <Col md={6} className="mb-3">
          <Form.Group>
            <Form.Label>Certificate Serial</Form.Label>
            <Form.Control
              type="text"
              value={serial}
              placeholder="In hexadecimal format (e.g. 01ab23cd45ef6789)"
              onChange={(e) => setSerial(e.target.value)}
            />
          </Form.Group>
        </Col>
        <Col md={6} className="mb-3">
          <Form.Group>
            <Form.Label>Reason</Form.Label>
            <Form.Select
              value={reason}
              onChange={(e) => setReason(Number(e.target.value))}
            >
              <option value={0}>Unspecified</option>
              <option value={1}>Key Compromise</option>
              <option value={2}>CA Compromise</option>
              <option value={3}>Affiliation Changed</option>
              <option value={4}>Superseded</option>
              <option value={5}>Cessation of Operation</option>
              <option value={6}>Certificate Hold</option>
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={12} className="d-flex justify-content-end mb-3">
          <Form.Group>
            <Button
              variant="danger"
              onClick={handleRevoke}
              disabled={!serial || isLoading}
            >
              Revoke Certificate
            </Button>
          </Form.Group>
        </Col>
      </Row>
    </Form>
  );
}

export default SignerDetails;
